/**
 * Metadata Validation Service
 *
 * Validates metadata files against defined schemas and provides detailed error reporting.
 * Supports templates, fields, datasets, and enrichment configurations.
 */

import {
  FIELD_TYPES,
  VALIDATION_TYPES,
  ENRICHMENT_SOURCES,
  TEMPLATE_SCHEMA,
  DATASET_SCHEMA,
  FIELD_SCHEMA,
  ENRICHMENT_SCHEMA,
} from "../schemas/metadata.js";

/**
 * Validation error class
 */
export class ValidationError extends Error {
  constructor(message, path = "", field = "", code = "VALIDATION_ERROR") {
    super(message);
    this.name = "ValidationError";
    this.path = path;
    this.field = field;
    this.code = code;
  }
}

/**
 * Validation result class
 */
export class ValidationResult {
  constructor() {
    this.valid = true;
    this.errors = [];
    this.warnings = [];
    this.parsed = null;
  }

  addError(message, path = "", field = "", code = "VALIDATION_ERROR") {
    this.valid = false;
    this.errors.push({
      message,
      path,
      field,
      code,
      severity: "error",
    });
  }

  addWarning(message, path = "", field = "", code = "VALIDATION_WARNING") {
    this.warnings.push({
      message,
      path,
      field,
      code,
      severity: "warning",
    });
  }

  merge(other) {
    if (!other.valid) {
      this.valid = false;
    }
    this.errors.push(...other.errors);
    this.warnings.push(...other.warnings);
  }

  getFormattedErrors() {
    return this.errors.map((error) => {
      const location = error.path ? `at ${error.path}` : "";
      const field = error.field ? `field "${error.field}"` : "";
      const context = [location, field].filter(Boolean).join(" ");
      return context ? `${error.message} ${context}` : error.message;
    });
  }
}

/**
 * Type validation utilities
 */
class TypeValidator {
  static isString(value) {
    return typeof value === "string";
  }

  static isNumber(value) {
    return typeof value === "number" && !isNaN(value);
  }

  static isBoolean(value) {
    return typeof value === "boolean";
  }

  static isArray(value) {
    return Array.isArray(value);
  }

  static isObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  static isValidDate(value) {
    if (typeof value === "string") {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    return value instanceof Date && !isNaN(value.getTime());
  }

  static isValidEmail(value) {
    if (!this.isString(value)) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  static isValidUrl(value) {
    if (!this.isString(value)) return false;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }

  static validateType(value, expectedType, path = "", field = "") {
    const result = new ValidationResult();

    switch (expectedType) {
      case "string":
        if (!this.isString(value)) {
          result.addError(
            `Expected string, got ${typeof value}`,
            path,
            field,
            "TYPE_MISMATCH"
          );
        }
        break;
      case "number":
        if (!this.isNumber(value)) {
          result.addError(
            `Expected number, got ${typeof value}`,
            path,
            field,
            "TYPE_MISMATCH"
          );
        }
        break;
      case "boolean":
        if (!this.isBoolean(value)) {
          result.addError(
            `Expected boolean, got ${typeof value}`,
            path,
            field,
            "TYPE_MISMATCH"
          );
        }
        break;
      case "array":
        if (!this.isArray(value)) {
          result.addError(
            `Expected array, got ${typeof value}`,
            path,
            field,
            "TYPE_MISMATCH"
          );
        }
        break;
      case "object":
        if (!this.isObject(value)) {
          result.addError(
            `Expected object, got ${typeof value}`,
            path,
            field,
            "TYPE_MISMATCH"
          );
        }
        break;
      case "datetime":
        if (!this.isValidDate(value)) {
          result.addError(
            `Expected valid datetime, got ${typeof value}`,
            path,
            field,
            "INVALID_DATETIME"
          );
        }
        break;
      case "email":
        if (!this.isValidEmail(value)) {
          result.addError(
            `Expected valid email address`,
            path,
            field,
            "INVALID_EMAIL"
          );
        }
        break;
      case "url":
        if (!this.isValidUrl(value)) {
          result.addError(`Expected valid URL`, path, field, "INVALID_URL");
        }
        break;
      case "any":
        // Any type is valid
        break;
      default:
        result.addError(
          `Unknown type: ${expectedType}`,
          path,
          field,
          "UNKNOWN_TYPE"
        );
    }

    return result;
  }
}

/**
 * Schema validation utilities
 */
class SchemaValidator {
  static validateObject(obj, schema, path = "") {
    const result = new ValidationResult();

    if (!TypeValidator.isObject(obj)) {
      result.addError(`Expected object`, path, "", "TYPE_MISMATCH");
      return result;
    }

    // Validate required fields
    for (const [key, expectedType] of Object.entries(schema)) {
      const fieldPath = path ? `${path}.${key}` : key;
      const value = obj[key];

      // Handle array schemas
      if (Array.isArray(expectedType)) {
        if (expectedType.length === 1) {
          // Array of specific type
          const itemType = expectedType[0];
          const arrayResult = TypeValidator.validateType(
            value,
            "array",
            fieldPath,
            key
          );
          result.merge(arrayResult);

          if (arrayResult.valid && Array.isArray(value)) {
            // Validate each array item
            for (let i = 0; i < value.length; i++) {
              const itemPath = `${fieldPath}[${i}]`;
              const itemResult = this.validateValue(
                value[i],
                itemType,
                itemPath,
                `${key}[${i}]`
              );
              result.merge(itemResult);
            }
          }
        }
        continue;
      }

      // Handle nested object schemas
      if (
        TypeValidator.isObject(expectedType) &&
        !this.isTypeString(expectedType)
      ) {
        if (value !== undefined) {
          const nestedResult = this.validateObject(
            value,
            expectedType,
            fieldPath
          );
          result.merge(nestedResult);
        }
        continue;
      }

      // Handle primitive types
      if (value !== undefined) {
        const typeResult = this.validateValue(
          value,
          expectedType,
          fieldPath,
          key
        );
        result.merge(typeResult);
      }
    }

    return result;
  }

  static validateValue(value, expectedType, path = "", field = "") {
    if (typeof expectedType === "string") {
      return TypeValidator.validateType(value, expectedType, path, field);
    }

    if (TypeValidator.isObject(expectedType)) {
      return this.validateObject(value, expectedType, path);
    }

    const result = new ValidationResult();
    result.addError(
      `Invalid schema type: ${typeof expectedType}`,
      path,
      field,
      "INVALID_SCHEMA"
    );
    return result;
  }

  static isTypeString(obj) {
    // Check if object represents a type definition rather than a nested schema
    return obj && typeof obj === "object" && Object.keys(obj).length === 0;
  }
}

/**
 * Specific metadata validators
 */
class TemplateValidator {
  static validate(template) {
    const result = new ValidationResult();

    // Basic structure validation
    const structureResult = SchemaValidator.validateObject(template, {
      id: "string",
      name: "string",
      description: "string",
      version: "string",
      category: "string",
    });
    result.merge(structureResult);

    if (!result.valid) {
      return result;
    }

    // Validate layout structure
    if (template.layout) {
      const layoutResult = this.validateLayout(template.layout);
      result.merge(layoutResult);
    }

    // Validate metadata
    if (template.metadata) {
      const metadataResult = this.validateMetadata(template.metadata);
      result.merge(metadataResult);
    }

    // Validate settings
    if (template.settings) {
      const settingsResult = this.validateSettings(template.settings);
      result.merge(settingsResult);
    }

    result.parsed = template;
    return result;
  }

  static validateLayout(layout) {
    const result = new ValidationResult();

    // Validate columns
    if (layout.columns !== undefined) {
      if (
        !TypeValidator.isNumber(layout.columns) ||
        layout.columns < 1 ||
        layout.columns > 4
      ) {
        result.addError(
          "Layout columns must be a number between 1 and 4",
          "layout.columns"
        );
      }
    }

    // Validate sections
    if (layout.sections) {
      if (!TypeValidator.isArray(layout.sections)) {
        result.addError("Layout sections must be an array", "layout.sections");
      } else {
        layout.sections.forEach((section, index) => {
          const sectionResult = this.validateSection(
            section,
            `layout.sections[${index}]`
          );
          result.merge(sectionResult);
        });
      }
    }

    return result;
  }

  static validateSection(section, path) {
    const result = new ValidationResult();

    // Required fields
    if (!TypeValidator.isString(section.id)) {
      result.addError(
        "Section id is required and must be a string",
        `${path}.id`
      );
    }

    if (!TypeValidator.isString(section.title)) {
      result.addError(
        "Section title is required and must be a string",
        `${path}.title`
      );
    }

    // Validate fields array
    if (section.fields) {
      if (!TypeValidator.isArray(section.fields)) {
        result.addError("Section fields must be an array", `${path}.fields`);
      } else {
        section.fields.forEach((field, index) => {
          if (!TypeValidator.isString(field)) {
            result.addError(
              `Field reference must be a string`,
              `${path}.fields[${index}]`
            );
          }
        });
      }
    }

    return result;
  }

  static validateMetadata(metadata) {
    const result = new ValidationResult();

    // Validate status
    if (metadata.status) {
      const validStatuses = ["draft", "active", "deprecated"];
      if (!validStatuses.includes(metadata.status)) {
        result.addError(
          `Invalid status. Must be one of: ${validStatuses.join(", ")}`,
          "metadata.status"
        );
      }
    }

    // Validate dates
    if (
      metadata.created_at &&
      !TypeValidator.isValidDate(metadata.created_at)
    ) {
      result.addError("Invalid created_at date format", "metadata.created_at");
    }

    if (
      metadata.updated_at &&
      !TypeValidator.isValidDate(metadata.updated_at)
    ) {
      result.addError("Invalid updated_at date format", "metadata.updated_at");
    }

    return result;
  }

  static validateSettings(settings) {
    const result = new ValidationResult();

    // Validate auto_save
    if (
      settings.auto_save !== undefined &&
      !TypeValidator.isBoolean(settings.auto_save)
    ) {
      result.addError("auto_save must be a boolean", "settings.auto_save");
    }

    // Validate save_interval
    if (settings.save_interval !== undefined) {
      if (
        !TypeValidator.isNumber(settings.save_interval) ||
        settings.save_interval < 1
      ) {
        result.addError(
          "save_interval must be a positive number",
          "settings.save_interval"
        );
      }
    }

    // Validate enrichment_trigger
    if (settings.enrichment_trigger) {
      const validTriggers = ["on_blur", "on_change", "manual"];
      if (!validTriggers.includes(settings.enrichment_trigger)) {
        result.addError(
          `Invalid enrichment_trigger. Must be one of: ${validTriggers.join(", ")}`,
          "settings.enrichment_trigger"
        );
      }
    }

    return result;
  }
}

class FieldValidator {
  static validate(field) {
    const result = new ValidationResult();

    // Basic structure validation
    const structureResult = SchemaValidator.validateObject(field, {
      id: "string",
      name: "string",
      label: "string",
      type: "string",
    });
    result.merge(structureResult);

    if (!result.valid) {
      return result;
    }

    // Validate field type
    const validTypes = Object.values(FIELD_TYPES);
    if (!validTypes.includes(field.type)) {
      result.addError(
        `Invalid field type: ${field.type}. Valid types: ${validTypes.join(", ")}`,
        "type"
      );
    }

    // Validate validation rules
    if (field.validation) {
      const validationResult = this.validateValidationRules(field.validation);
      result.merge(validationResult);
    }

    // Validate field configuration
    if (field.config) {
      const configResult = this.validateFieldConfig(field.config, field.type);
      result.merge(configResult);
    }

    // Validate enrichment configuration
    if (field.enrichment) {
      const enrichmentResult = this.validateEnrichmentConfig(field.enrichment);
      result.merge(enrichmentResult);
    }

    // Validate conditional logic
    if (field.conditional) {
      const conditionalResult = this.validateConditionalConfig(
        field.conditional
      );
      result.merge(conditionalResult);
    }

    result.parsed = field;
    return result;
  }

  static validateValidationRules(validation) {
    const result = new ValidationResult();

    if (!TypeValidator.isArray(validation)) {
      result.addError("Validation must be an array", "validation");
      return result;
    }

    const validTypes = Object.values(VALIDATION_TYPES);
    validation.forEach((rule, index) => {
      const path = `validation[${index}]`;

      if (!TypeValidator.isObject(rule)) {
        result.addError("Validation rule must be an object", path);
        return;
      }

      if (
        !TypeValidator.isString(rule.type) ||
        !validTypes.includes(rule.type)
      ) {
        result.addError(
          `Invalid validation type: ${rule.type}. Valid types: ${validTypes.join(", ")}`,
          `${path}.type`
        );
      }

      // Type-specific validation
      switch (rule.type) {
        case "min_length":
        case "max_length":
        case "min_value":
        case "max_value":
          if (!TypeValidator.isNumber(rule.value) || rule.value < 0) {
            result.addError(
              `${rule.type} value must be a positive number`,
              `${path}.value`
            );
          }
          break;
        case "pattern":
          if (!TypeValidator.isString(rule.value)) {
            result.addError("Pattern value must be a string", `${path}.value`);
          } else {
            try {
              new RegExp(rule.value);
            } catch (e) {
              result.addError("Invalid regex pattern", `${path}.value`);
            }
          }
          break;
      }
    });

    return result;
  }

  static validateFieldConfig(config, fieldType) {
    const result = new ValidationResult();

    // Validate width
    if (config.width) {
      const validWidths = ["full", "half", "third", "quarter"];
      if (!validWidths.includes(config.width)) {
        result.addError(
          `Invalid width: ${config.width}. Valid widths: ${validWidths.join(", ")}`,
          "config.width"
        );
      }
    }

    // Type-specific config validation
    switch (fieldType) {
      case FIELD_TYPES.SELECT:
      case FIELD_TYPES.MULTI_SELECT:
      case FIELD_TYPES.RADIO:
        if (config.options && !TypeValidator.isArray(config.options)) {
          result.addError(
            "Options must be an array for select/radio fields",
            "config.options"
          );
        }
        break;

      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.CURRENCY:
        if (config.min !== undefined && !TypeValidator.isNumber(config.min)) {
          result.addError("Min value must be a number", "config.min");
        }
        if (config.max !== undefined && !TypeValidator.isNumber(config.max)) {
          result.addError("Max value must be a number", "config.max");
        }
        break;

      case FIELD_TYPES.TEXTAREA:
        if (
          config.rows !== undefined &&
          (!TypeValidator.isNumber(config.rows) || config.rows < 1)
        ) {
          result.addError("Rows must be a positive number", "config.rows");
        }
        break;
    }

    return result;
  }

  static validateEnrichmentConfig(enrichment) {
    const result = new ValidationResult();

    // Validate source
    const validSources = Object.values(ENRICHMENT_SOURCES);
    if (!validSources.includes(enrichment.source)) {
      result.addError(
        `Invalid enrichment source: ${enrichment.source}. Valid sources: ${validSources.join(", ")}`,
        "enrichment.source"
      );
    }

    // Validate trigger
    if (enrichment.trigger) {
      const validTriggers = ["on_blur", "on_change", "manual"];
      if (!validTriggers.includes(enrichment.trigger)) {
        result.addError(
          `Invalid enrichment trigger: ${enrichment.trigger}. Valid triggers: ${validTriggers.join(", ")}`,
          "enrichment.trigger"
        );
      }
    }

    return result;
  }

  static validateConditionalConfig(conditional) {
    const result = new ValidationResult();

    // Validate conditional operators
    const validOperators = [
      "equals",
      "not_equals",
      "contains",
      "not_contains",
      "greater_than",
      "less_than",
      "not_empty",
      "empty",
    ];

    ["show_when", "enable_when", "require_when"].forEach((condType) => {
      if (conditional[condType]) {
        const cond = conditional[condType];
        const path = `conditional.${condType}`;

        if (!TypeValidator.isObject(cond)) {
          result.addError(`${condType} must be an object`, path);
          return;
        }

        if (!TypeValidator.isString(cond.field)) {
          result.addError(
            "Conditional field must be a string",
            `${path}.field`
          );
        }

        if (!validOperators.includes(cond.operator)) {
          result.addError(
            `Invalid conditional operator: ${cond.operator}. Valid operators: ${validOperators.join(", ")}`,
            `${path}.operator`
          );
        }
      }
    });

    return result;
  }
}

class EnrichmentValidator {
  static validate(enrichment) {
    const result = new ValidationResult();

    // Basic structure validation
    const structureResult = SchemaValidator.validateObject(enrichment, {
      id: "string",
      name: "string",
      source: "string",
    });
    result.merge(structureResult);

    if (!result.valid) {
      return result;
    }

    // Validate source
    const validSources = Object.values(ENRICHMENT_SOURCES);
    if (!validSources.includes(enrichment.source)) {
      result.addError(
        `Invalid enrichment source: ${enrichment.source}. Valid sources: ${validSources.join(", ")}`,
        "source"
      );
    }

    // Validate API configuration
    if (enrichment.api) {
      const apiResult = this.validateApiConfig(enrichment.api);
      result.merge(apiResult);
    }

    // Validate response configuration
    if (enrichment.response) {
      const responseResult = this.validateResponseConfig(enrichment.response);
      result.merge(responseResult);
    }

    result.parsed = enrichment;
    return result;
  }

  static validateApiConfig(api) {
    const result = new ValidationResult();

    if (!TypeValidator.isValidUrl(api.base_url)) {
      result.addError("API base_url must be a valid URL", "api.base_url");
    }

    if (api.method) {
      const validMethods = ["GET", "POST", "PUT", "DELETE"];
      if (!validMethods.includes(api.method)) {
        result.addError(
          `Invalid HTTP method: ${api.method}. Valid methods: ${validMethods.join(", ")}`,
          "api.method"
        );
      }
    }

    if (
      api.timeout !== undefined &&
      (!TypeValidator.isNumber(api.timeout) || api.timeout < 1)
    ) {
      result.addError("API timeout must be a positive number", "api.timeout");
    }

    return result;
  }

  static validateResponseConfig(response) {
    const result = new ValidationResult();

    if (response.mappings && !TypeValidator.isArray(response.mappings)) {
      result.addError(
        "Response mappings must be an array",
        "response.mappings"
      );
    }

    return result;
  }
}

/**
 * Main validation functions
 */

/**
 * Validate template metadata
 * @param {Object} template - Template object to validate
 * @returns {ValidationResult} - Validation result
 */
export function validateTemplate(template) {
  try {
    return TemplateValidator.validate(template);
  } catch (error) {
    const result = new ValidationResult();
    result.addError(
      `Validation error: ${error.message}`,
      "",
      "",
      "VALIDATION_EXCEPTION"
    );
    return result;
  }
}

/**
 * Validate field metadata
 * @param {Object} field - Field object to validate
 * @returns {ValidationResult} - Validation result
 */
export function validateField(field) {
  try {
    return FieldValidator.validate(field);
  } catch (error) {
    const result = new ValidationResult();
    result.addError(
      `Validation error: ${error.message}`,
      "",
      "",
      "VALIDATION_EXCEPTION"
    );
    return result;
  }
}

/**
 * Validate fields array
 * @param {Array} fields - Array of field objects to validate
 * @returns {ValidationResult} - Validation result
 */
export function validateFields(fields) {
  const result = new ValidationResult();

  if (!TypeValidator.isArray(fields)) {
    result.addError("Fields must be an array");
    return result;
  }

  // Track field IDs for uniqueness check
  const fieldIds = new Set();

  fields.forEach((field, index) => {
    const fieldResult = validateField(field);

    // Add path context to errors
    fieldResult.errors.forEach((error) => {
      error.path = error.path ? `[${index}].${error.path}` : `[${index}]`;
    });

    result.merge(fieldResult);

    // Check for duplicate IDs
    if (field.id) {
      if (fieldIds.has(field.id)) {
        result.addError(`Duplicate field ID: ${field.id}`, `[${index}].id`);
      } else {
        fieldIds.add(field.id);
      }
    }
  });

  if (result.valid) {
    result.parsed = fields;
  }

  return result;
}

/**
 * Validate enrichment metadata
 * @param {Object} enrichment - Enrichment object to validate
 * @returns {ValidationResult} - Validation result
 */
export function validateEnrichment(enrichment) {
  try {
    return EnrichmentValidator.validate(enrichment);
  } catch (error) {
    const result = new ValidationResult();
    result.addError(
      `Validation error: ${error.message}`,
      "",
      "",
      "VALIDATION_EXCEPTION"
    );
    return result;
  }
}

/**
 * Validate any metadata based on type detection
 * @param {Object} metadata - Metadata object to validate
 * @param {string} type - Optional type hint ('template', 'field', 'enrichment')
 * @returns {ValidationResult} - Validation result
 */
export function validateMetadata(metadata, type = null) {
  const result = new ValidationResult();

  if (!TypeValidator.isObject(metadata)) {
    result.addError("Metadata must be an object");
    return result;
  }

  // Auto-detect type if not provided
  if (!type) {
    if (metadata.layout || metadata.sections) {
      type = "template";
    } else if (
      metadata.type &&
      Object.values(FIELD_TYPES).includes(metadata.type)
    ) {
      type = "field";
    } else if (
      metadata.source &&
      Object.values(ENRICHMENT_SOURCES).includes(metadata.source)
    ) {
      type = "enrichment";
    }
  }

  // Validate based on type
  switch (type) {
    case "template":
      return validateTemplate(metadata);
    case "field":
      return validateField(metadata);
    case "enrichment":
      return validateEnrichment(metadata);
    default:
      result.addError(
        `Unknown or undetectable metadata type. Please specify type explicitly.`
      );
      return result;
  }
}

/**
 * Parse and validate metadata with error collection
 * @param {Object} rawMetadata - Raw metadata to parse and validate
 * @param {string} type - Metadata type
 * @returns {Object} - Parsed metadata with validation results
 */
export function parseMetadata(rawMetadata, type = null) {
  const validationResult = validateMetadata(rawMetadata, type);

  return {
    valid: validationResult.valid,
    data: validationResult.parsed || rawMetadata,
    errors: validationResult.errors,
    warnings: validationResult.warnings,
    type: type || "unknown",
  };
}

// Export the service interface
export default {
  validateTemplate,
  validateField,
  validateFields,
  validateEnrichment,
  validateMetadata,
  parseMetadata,
  ValidationError,
  ValidationResult,
};
